'use client'

import { useState } from 'react'
import AuthShell from '@/components/AuthShell'
import { confirmPasswordReset, requestPasswordReset } from '@/lib/auth/cognito'

export default function ResetPasswordPage() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [step, setStep] = useState<'request' | 'confirm'>('request')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const requestReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    try {
      setIsSubmitting(true)
      await requestPasswordReset(email)
      setStep('confirm')
      setSuccess('Verification code sent. Check your email.')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to request reset.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const confirmReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    try {
      setIsSubmitting(true)
      await confirmPasswordReset(email, code, newPassword)
      setSuccess('Password reset complete. You can now log in.')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell
      title="Reset Password"
      subtitle="Request a code, then set a new password."
      footerText="Remember your password?"
      footerLinkHref="/en/login"
      footerLinkLabel="Back to login"
    >
      {step === 'request' ? (
        <form onSubmit={requestReset} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-brand-charcoal mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-brand-cool-gray rounded-lg focus:ring-2 focus:ring-brand-green outline-none"
            />
          </div>

          {error ? <p className="text-sm text-brand-red">{error}</p> : null}
          {success ? <p className="text-sm text-brand-green">{success}</p> : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-lg font-semibold text-white bg-brand-green hover:bg-brand-green-deep disabled:bg-brand-muted"
          >
            {isSubmitting ? 'Requesting...' : 'Request reset code'}
          </button>
        </form>
      ) : (
        <form onSubmit={confirmReset} className="space-y-5">
          <div>
            <label htmlFor="code" className="block text-sm font-semibold text-brand-charcoal mb-1">
              Verification code
            </label>
            <input
              id="code"
              type="text"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-4 py-3 border border-brand-cool-gray rounded-lg focus:ring-2 focus:ring-brand-green outline-none"
            />
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-semibold text-brand-charcoal mb-1">
              New password
            </label>
            <input
              id="newPassword"
              type="password"
              minLength={8}
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 border border-brand-cool-gray rounded-lg focus:ring-2 focus:ring-brand-green outline-none"
            />
          </div>

          {error ? <p className="text-sm text-brand-red">{error}</p> : null}
          {success ? <p className="text-sm text-brand-green">{success}</p> : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-lg font-semibold text-white bg-brand-green hover:bg-brand-green-deep disabled:bg-brand-muted"
          >
            {isSubmitting ? 'Saving...' : 'Set new password'}
          </button>
        </form>
      )}
    </AuthShell>
  )
}
